import React, { useState } from 'react';
import { ArrowRight, Shield, AlertTriangle, Clock, Phone, CheckCircle, Zap, Users } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ConsultationModal from '../../components/ConsultationModal';
import DemoModal from '../../components/DemoModal';

const IncidentResponsePage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const responseServices = [
    {
      icon: Zap,
      title: "24/7 Emergency Response",
      description: "Immediate response to security incidents with expert incident handlers available around the clock.",
      features: ["Rapid containment", "Threat neutralization", "Evidence preservation", "Communication support"]
    },
    {
      icon: AlertTriangle,
      title: "Forensic Investigation",
      description: "Comprehensive digital forensics to understand the scope and impact of security incidents.",
      features: ["Evidence collection", "Timeline reconstruction", "Root cause analysis", "Legal documentation"]
    },
    {
      icon: Users,
      title: "Crisis Management",
      description: "Strategic incident management and communication to minimize business impact and reputation damage.",
      features: ["Stakeholder communication", "Media relations", "Regulatory reporting", "Business continuity"]
    },
    {
      icon: Shield,
      title: "Recovery & Remediation",
      description: "Complete system recovery and security improvements to prevent future incidents.",
      features: ["System restoration", "Security hardening", "Vulnerability patching", "Process improvement"]
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-slate-50'
    }`}>
      <Header 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />

      <ConsultationModal 
        isOpen={isConsultationModalOpen} 
        onClose={() => setIsConsultationModalOpen(false)} 
      />
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />

      {/* Hero Section */}
      <section className={`relative py-20 overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-red-950 text-slate-100' 
          : 'bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 text-slate-900'
      }`}>
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/5380664/pexels-photo-5380664.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Incident Response"
            className="w-full h-full object-cover opacity-10"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-slate-950/90 to-red-950/90' 
              : 'bg-gradient-to-r from-red-50/90 to-yellow-50/90'
          }`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <AlertTriangle className={`h-16 w-16 mx-auto mb-6 ${
              isDarkMode ? 'text-red-400' : 'text-red-600'
            }`} />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Incident <span className={isDarkMode ? "text-red-400" : "text-red-600"}>Response</span>
            </h1>
            <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}>
              Rapid response to security incidents with expert crisis management, 
              forensic investigation, and complete recovery services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsConsultationModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-lg ${
                  isDarkMode 
                    ? "bg-red-600 hover:bg-red-700 text-white hover:shadow-red-500/25" 
                    : "bg-red-600 hover:bg-red-700 text-white hover:shadow-red-500/25"
                }`}
              >
                <Phone className="mr-2 h-5 w-5" />
                Emergency Hotline
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 border-2 ${
                  isDarkMode 
                    ? "border-red-400 text-red-400 hover:bg-red-400 hover:text-slate-900" 
                    : "border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                }`}
              >
                Response Plan
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Response Services Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Comprehensive Incident Response
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              End-to-end incident response services to minimize damage and restore operations
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {responseServices.map((service, index) => (
              <div key={index} className={`p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow ${
                isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'
              }`}>
                <service.icon className={`h-12 w-12 mb-4 ${
                  isDarkMode ? 'text-red-400' : 'text-red-600'
                }`} />
                <h3 className={`text-xl font-semibold mb-3 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{service.title}</h3>
                <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle className={`h-4 w-4 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer 
        onConsultationClick={() => setIsConsultationModalOpen(true)}
        onDemoClick={() => setIsDemoModalOpen(true)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default IncidentResponsePage;